import { useEffect, useState } from "react";
import type { Panel } from "@electro-planner/domain";

/**
 * Dialog som visar revisionshistorik och låter användaren skapa en ny
 * snapshot (Rev A, Rev B, …). Snapshot:arna ligger på panelen själv och
 * sparas i samma Yjs-doc — inte i separat CRDT-list.
 *
 * Återställning av en revision ingår inte i MVP. Användaren får ladda ner
 * snapshoten som JSON och kan i värsta fall återskapa manuellt.
 */
export function RevisionHistoryDialog({
  open,
  panel,
  onClose,
  onTakeSnapshot,
}: {
  open: boolean;
  panel: Panel | undefined;
  onClose: () => void;
  onTakeSnapshot: (label: string, note?: string) => void;
}) {
  const [label, setLabel] = useState("");
  const [note, setNote] = useState("");

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const revisions = (panel?.revisions ?? []).slice().reverse();

  const suggestedLabel = `Rev ${String.fromCharCode(65 + (panel?.revisions?.length ?? 0))}`;

  const create = () => {
    const lbl = label.trim() || suggestedLabel;
    onTakeSnapshot(lbl, note);
    setLabel("");
    setNote("");
  };

  const download = (id: string, snap: string) => {
    const blob = new Blob([snap], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${panel?.designation ?? "central"}__${id}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4"
      onClick={onClose}
    >
      <div
        className="flex max-h-[80vh] w-full max-w-2xl flex-col overflow-hidden rounded-lg bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
          <h2 className="text-lg font-semibold text-slate-800">
            Revisionshistorik
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded px-2 py-1 text-slate-500 hover:bg-slate-100"
            aria-label="Stäng"
          >
            ×
          </button>
        </div>

        <div className="border-b border-slate-200 p-4">
          <div className="mb-2 text-xs text-slate-500">
            Skapa en ny revision när du vill spara nuvarande state som en
            tidpunkt. Revisionerna följer med dokumentet.
          </div>
          <div className="flex items-end gap-2">
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-slate-700">
                Namn
              </span>
              <input
                type="text"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder={suggestedLabel}
                className="rounded border border-slate-300 px-2 py-1 text-sm"
              />
            </label>
            <label className="flex flex-1 flex-col gap-1">
              <span className="text-xs font-medium text-slate-700">
                Kommentar
              </span>
              <input
                type="text"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Vad ändrades?"
                className="rounded border border-slate-300 px-2 py-1 text-sm"
              />
            </label>
            <button
              type="button"
              onClick={create}
              className="rounded bg-sky-600 px-3 py-1 text-sm text-white hover:bg-sky-700"
            >
              + Skapa revision
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-auto p-4">
          {revisions.length === 0 ? (
            <div className="py-6 text-center text-sm text-slate-500">
              Inga revisioner sparade än.
            </div>
          ) : (
            <ul className="space-y-2">
              {revisions.map((r) => (
                <li
                  key={r.id}
                  className="rounded border border-slate-200 bg-slate-50 p-3 text-sm"
                >
                  <div className="flex items-center justify-between">
                    <span className="font-mono font-semibold text-slate-700">
                      {r.label}
                    </span>
                    <time className="text-xs text-slate-500">
                      {new Date(r.createdAt).toLocaleString("sv-SE")}
                    </time>
                  </div>
                  {r.note ? (
                    <p className="mt-1 text-slate-600">{r.note}</p>
                  ) : null}
                  <div className="mt-2">
                    <button
                      type="button"
                      onClick={() => download(r.id, r.snapshot)}
                      className="text-xs text-sky-700 hover:underline"
                    >
                      Ladda ner som JSON
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
